'use client';

import { usePublicContent } from '@/lib/use-public-content';
import type { LicensesCertificatesRegistry } from '@/types/content';
import { useTranslations } from 'next-intl';

import CertificateCard from './CertificateCard';

type CertificateCopy = {
    title?: string;
    description?: string;
};

function localizeCertificate(
    certificate: LicensesCertificatesRegistry['certificates'][number],
    translations: Record<string, CertificateCopy> | undefined,
) {
    const copy = translations?.[certificate.id];

    return {
        ...certificate,
        title: copy?.title?.trim() || certificate.title,
        description: copy?.description?.trim() || certificate.description,
    };
}

export default function LicensesCertificatesClient({
    initialRegistry,
}: {
    initialRegistry: LicensesCertificatesRegistry;
}) {
    const registry = usePublicContent('licenses-certificates', initialRegistry);
    const t = useTranslations('About');
    const certificateTranslations = t.raw('certificates') as
        | Record<string, CertificateCopy>
        | undefined;

    if (!registry.visible || registry.certificates.length === 0) {
        return null;
    }

    return (
        <section className="w-full bg-white pb-20">
            <div className="container mx-auto flex flex-col items-center gap-[60px] px-4 lg:px-0">
                <h2 className="w-full text-center text-h1 leading-tight text-dark-grey max-lg:text-h3">
                    {t('licensesAndCertifications')}
                </h2>
                <div className="mx-auto grid w-full max-w-[1110px] grid-cols-1 gap-[30px] md:grid-cols-2 lg:grid-cols-3">
                    {registry.certificates.map((certificate) => (
                        <CertificateCard
                            key={certificate.id}
                            certificate={localizeCertificate(certificate, certificateTranslations)}
                            downloadLabel={t('downloadCertificate')}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
